/*
 * Alorae Web Component base class.
 * Every new component in `assets/components/*` and inline `{% javascript %}`
 * blocks should extend this. See docs/alorae-rebuild/decompose-theme-files-roadmap.md
 * §Phase 4E.
 */

/**
 * Base class for all Alorae Web Components.
 *
 * Provides:
 *   - Lifecycle hooks: `init()` / `cleanup()` invoked on connect/disconnect.
 *   - Tracked listener registration so global handlers are torn down safely.
 *   - `dispatch(name, detail)` — bubbling + composed CustomEvents.
 *   - Lazy `this.refs.foo` lookup of `[data-ref="foo"]` descendants (cached).
 *   - Shopify theme-editor event routing to `onSectionSelect()` /
 *     `onSectionUnload()` / `onSectionLoad()` / `onBlockSelect()` hooks,
 *     scoped to the section containing this element.
 *
 * @example
 *   import { Component } from '{{ "component.js" | asset_url }}';
 *
 *   class MiniCart extends Component {
 *     init() {
 *       this.addListener(this.refs.toggle, 'click', () => this.open());
 *       this.addListener(document, 'keydown', (e) => {
 *         if (e.key === 'Escape') this.close();
 *       });
 *     }
 *     open() {
 *       this.setAttribute('open', '');
 *       this.dispatch('mini-cart:open');
 *     }
 *     close() { this.removeAttribute('open'); }
 *     onSectionSelect() { this.open(); }
 *   }
 *
 *   Component.define('mini-cart', MiniCart);
 */
export class Component extends HTMLElement {
  constructor() {
    super();
    /** @type {Array<{target: EventTarget, event: string, handler: EventListenerOrEventListenerObject, options?: AddEventListenerOptions | boolean}>} */
    this._listeners = [];
    /** @type {Record<string, Element | null> | null} */
    this._refsCache = null;
    this._onShopifySectionSelect = this._onShopifySectionSelect.bind(this);
    this._onShopifySectionUnload = this._onShopifySectionUnload.bind(this);
    this._onShopifySectionLoad = this._onShopifySectionLoad.bind(this);
    this._onShopifyBlockSelect = this._onShopifyBlockSelect.bind(this);
  }

  /**
   * Lazy-computed proxy mapping `[data-ref="X"]` descendants. First read of
   * `this.refs.foo` runs `this.querySelector('[data-ref="foo"]')` and caches
   * the result. Reset the cache by setting `this._refsCache = null`.
   */
  get refs() {
    if (!this._refsCache) {
      const cache = Object.create(null);
      const host = this;
      this._refsCache = new Proxy(cache, {
        get(store, name) {
          if (typeof name !== 'string') return store[name];
          if (!(name in store)) {
            store[name] = host.querySelector(`[data-ref="${name}"]`);
          }
          return store[name];
        },
      });
    }
    return this._refsCache;
  }

  /**
   * Returns the `data-section-id` of the section this component belongs to,
   * or `null` if none. Used to scope Shopify theme-editor events.
   * @returns {string | null}
   */
  get sectionId() {
    const wrapper = this.closest('[data-section-id]');
    return wrapper ? wrapper.getAttribute('data-section-id') : null;
  }

  connectedCallback() {
    // Theme-editor event routing — only attach if the subclass cares.
    if (typeof this.onSectionSelect === 'function') {
      document.addEventListener('shopify:section:select', this._onShopifySectionSelect);
    }
    if (typeof this.onSectionUnload === 'function') {
      document.addEventListener('shopify:section:unload', this._onShopifySectionUnload);
    }
    if (typeof this.onSectionLoad === 'function') {
      document.addEventListener('shopify:section:load', this._onShopifySectionLoad);
    }
    if (typeof this.onBlockSelect === 'function') {
      document.addEventListener('shopify:block:select', this._onShopifyBlockSelect);
    }
    if (typeof this.init === 'function') {
      try {
        this.init();
      } catch (err) {
        console.error(`[${this.tagName.toLowerCase()}] init() failed`, err);
      }
    }
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:section:select', this._onShopifySectionSelect);
    document.removeEventListener('shopify:section:unload', this._onShopifySectionUnload);
    document.removeEventListener('shopify:section:load', this._onShopifySectionLoad);
    document.removeEventListener('shopify:block:select', this._onShopifyBlockSelect);

    // Tear down every listener registered via addListener().
    for (const entry of this._listeners) {
      try {
        entry.target.removeEventListener(entry.event, entry.handler, entry.options);
      } catch (_) {
        /* target may be gone; ignore */
      }
    }
    this._listeners.length = 0;
    this._refsCache = null;

    if (typeof this.cleanup === 'function') {
      try {
        this.cleanup();
      } catch (err) {
        console.error(`[${this.tagName.toLowerCase()}] cleanup() failed`, err);
      }
    }
  }

  /**
   * Register a DOM event listener that will be removed automatically when
   * this component is disconnected. Use this instead of bare
   * `addEventListener` for any listener attached to `document`, `window`, or
   * an external element.
   *
   * @param {EventTarget} target
   * @param {string} event
   * @param {EventListenerOrEventListenerObject} handler
   * @param {AddEventListenerOptions | boolean} [options]
   */
  addListener(target, event, handler, options) {
    if (!target || typeof target.addEventListener !== 'function') return;
    target.addEventListener(event, handler, options);
    this._listeners.push({ target, event, handler, options });
  }

  /**
   * Dispatch a CustomEvent from this element. Bubbles and crosses shadow
   * boundaries by default — set `options.bubbles` / `options.composed` to
   * override.
   *
   * @param {string} eventName
   * @param {*} [detail]
   * @param {{bubbles?: boolean, composed?: boolean, cancelable?: boolean}} [options]
   * @returns {boolean} false if cancelled
   */
  dispatch(eventName, detail = null, options = {}) {
    return this.dispatchEvent(
      new CustomEvent(eventName, {
        detail,
        bubbles: options.bubbles !== false,
        composed: options.composed !== false,
        cancelable: options.cancelable === true,
      })
    );
  }

  /* ---------- Internal Shopify event routing ---------- */

  _matchesSection(event) {
    const sid = this.sectionId;
    return sid !== null && event.detail && String(event.detail.sectionId) === String(sid);
  }

  _onShopifySectionSelect(event) {
    if (this._matchesSection(event)) this.onSectionSelect(event);
  }

  _onShopifySectionUnload(event) {
    if (this._matchesSection(event)) this.onSectionUnload(event);
  }

  _onShopifySectionLoad(event) {
    if (this._matchesSection(event)) this.onSectionLoad(event);
  }

  _onShopifyBlockSelect(event) {
    // Block events carry sectionId of the parent section.
    if (this._matchesSection(event)) this.onBlockSelect(event);
  }

  /**
   * Idempotent wrapper around `customElements.define`. Safe to call from
   * multiple bundles or after a hot reload.
   *
   * @param {string} tagName
   * @param {CustomElementConstructor} ClassRef
   */
  static define(tagName, ClassRef) {
    if (!tagName || typeof tagName !== 'string') {
      throw new TypeError('Component.define: tagName must be a non-empty string');
    }
    if (typeof customElements === 'undefined') return;
    if (customElements.get(tagName)) return;
    customElements.define(tagName, ClassRef);
  }
}

export default Component;
